// SPDX-License-Identifier: Apache-2.0

// tfidfSpMV — TypeScript dispatch wrapper for the sparse posting SpMV kernel.
// Two compute passes per query: (1) scatter atomicAdd into u32 q16.16, (2) postpass to f32.
//
// Diagnostic discipline (matches search.ts pattern):
//   - getCompilationInfo() after createShaderModule, throw on errors
//   - pushErrorScope("validation") around encoder + submit
//   - sanity check: throw if scores have no nonzero spread

import shaderSrc from "./tfidfSpMV.wgsl?raw";
import postpassSrc from "./tfidfSpMV_postpass.wgsl?raw";

export interface TfidfSpMVSetup {
  device: GPUDevice;
  scatterPipeline: GPUComputePipeline;
  scatterBindGroupLayout: GPUBindGroupLayout;
  postpassPipeline: GPUComputePipeline;
  postpassBindGroupLayout: GPUBindGroupLayout;

  // Per-query rebound buffers
  paramsBuf: GPUBuffer;
  postpassParamsBuf: GPUBuffer;
  qTermBuf: GPUBuffer;
  qWBuf: GPUBuffer;

  // CSR posting list (resident — written once via uploadCorpus)
  termOffsetsBuf: GPUBuffer;
  postingDocBuf: GPUBuffer;
  postingWBuf: GPUBuffer;

  // Scatter target + readback
  scoresFixedBuf: GPUBuffer;
  scoresF32Buf: GPUBuffer;
  readbackBuf: GPUBuffer;

  vocabSize: number;
  totalPostings: number;
  nDocs: number;
  maxQueryTerms: number;
}

export interface TfidfSpMVResult {
  /** Per-doc dense score vector, length = nDocs. */
  scores: Float32Array;
  /** Wall time from encoder start through readback unmap. */
  totalMs: number;
  /** Min/max across the score vector — diagnostic, catches silent kernel failures. */
  scoreRange: { min: number; max: number };
  /** Number of docs that received any contribution. */
  nonzeroCount: number;
}

const WG = 64;
const FIXED_SCALE = 65536.0;
// How many workgroups along the x-dim to dispatch per query term.
// Picks up parallelism across long posting lists; small overhead for short ones.
const WORKGROUPS_PER_TERM = 16;
// Default ceiling on query length — buffer is sized to this once at setup.
const DEFAULT_MAX_QUERY_TERMS = 64;

export async function setupTfidfSpMV(
  device: GPUDevice,
  vocabSize: number,
  totalPostings: number,
  nDocs: number,
  maxQueryTerms: number = DEFAULT_MAX_QUERY_TERMS,
): Promise<TfidfSpMVSetup> {
  if (vocabSize < 1) throw new Error("vocabSize must be >= 1");
  if (totalPostings < 1) throw new Error("totalPostings must be >= 1");
  if (nDocs < 1) throw new Error("nDocs must be >= 1");
  if (maxQueryTerms < 1) throw new Error("maxQueryTerms must be >= 1");

  // ---- scatter pipeline ----
  const scatterModule = device.createShaderModule({ code: shaderSrc, label: "tfidfSpMV-scatter" });
  await assertNoCompileErrors(scatterModule, "tfidfSpMV scatter");

  const scatterBindGroupLayout = device.createBindGroupLayout({
    label: "tfidfSpMV-scatter-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // term_offsets
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // posting_doc
      { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // posting_w
      { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // q_term
      { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // q_w
      { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // scores_fixed (atomic<u32>)
    ],
  });
  const scatterPipeline = device.createComputePipeline({
    label: "tfidfSpMV-scatter",
    layout: device.createPipelineLayout({ bindGroupLayouts: [scatterBindGroupLayout] }),
    compute: { module: scatterModule, entryPoint: "main" },
  });

  // ---- postpass pipeline ----
  const postpassModule = device.createShaderModule({ code: postpassSrc, label: "tfidfSpMV-postpass" });
  await assertNoCompileErrors(postpassModule, "tfidfSpMV postpass");

  const postpassBindGroupLayout = device.createBindGroupLayout({
    label: "tfidfSpMV-postpass-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // scores_fixed (read as plain u32)
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // scores_f32
    ],
  });
  const postpassPipeline = device.createComputePipeline({
    label: "tfidfSpMV-postpass",
    layout: device.createPipelineLayout({ bindGroupLayouts: [postpassBindGroupLayout] }),
    compute: { module: postpassModule, entryPoint: "main" },
  });

  // ---- buffers ----
  const paramsBuf = device.createBuffer({
    label: "tfidfSpMV-params",
    size: 16, // 4 × u32, std140-aligned
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const postpassParamsBuf = device.createBuffer({
    label: "tfidfSpMV-postpass-params",
    size: 16, // u32 + f32, padded
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const qTermBuf = device.createBuffer({
    label: "tfidfSpMV-q-term",
    size: maxQueryTerms * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const qWBuf = device.createBuffer({
    label: "tfidfSpMV-q-w",
    size: maxQueryTerms * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });

  const termOffsetsBuf = device.createBuffer({
    label: "tfidfSpMV-term-offsets",
    size: (vocabSize + 1) * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const postingDocBuf = device.createBuffer({
    label: "tfidfSpMV-posting-doc",
    size: totalPostings * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const postingWBuf = device.createBuffer({
    label: "tfidfSpMV-posting-w",
    size: totalPostings * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });

  const scoresFixedBuf = device.createBuffer({
    label: "tfidfSpMV-scores-fixed",
    size: nDocs * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
  });
  const scoresF32Buf = device.createBuffer({
    label: "tfidfSpMV-scores-f32",
    size: nDocs * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const readbackBuf = device.createBuffer({
    label: "tfidfSpMV-readback",
    size: nDocs * 4,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });

  // Postpass params are static — write once.
  const postpassParams = new ArrayBuffer(16);
  new Uint32Array(postpassParams, 0, 1)[0] = nDocs;
  new Float32Array(postpassParams, 4, 1)[0] = 1.0 / FIXED_SCALE;
  device.queue.writeBuffer(postpassParamsBuf, 0, postpassParams);

  return {
    device,
    scatterPipeline,
    scatterBindGroupLayout,
    postpassPipeline,
    postpassBindGroupLayout,
    paramsBuf,
    postpassParamsBuf,
    qTermBuf,
    qWBuf,
    termOffsetsBuf,
    postingDocBuf,
    postingWBuf,
    scoresFixedBuf,
    scoresF32Buf,
    readbackBuf,
    vocabSize,
    totalPostings,
    nDocs,
    maxQueryTerms,
  };
}

/**
 * Upload the CSR posting list to the GPU. Run once per vault load (or on rebuild).
 * `termOffsets` length must equal vocabSize + 1.
 * `postingDoc` and `postingW` must have length equal to totalPostings.
 */
export function uploadPostings(
  setup: TfidfSpMVSetup,
  termOffsets: Uint32Array,
  postingDoc: Uint32Array,
  postingW: Float32Array,
): void {
  if (termOffsets.length !== setup.vocabSize + 1) {
    throw new Error(`termOffsets length ${termOffsets.length} must equal vocabSize+1 = ${setup.vocabSize + 1}`);
  }
  if (postingDoc.length !== setup.totalPostings) {
    throw new Error(`postingDoc length ${postingDoc.length} must equal totalPostings = ${setup.totalPostings}`);
  }
  if (postingW.length !== setup.totalPostings) {
    throw new Error(`postingW length ${postingW.length} must equal totalPostings = ${setup.totalPostings}`);
  }
  setup.device.queue.writeBuffer(
    setup.termOffsetsBuf, 0,
    termOffsets.buffer, termOffsets.byteOffset, termOffsets.byteLength,
  );
  setup.device.queue.writeBuffer(
    setup.postingDocBuf, 0,
    postingDoc.buffer, postingDoc.byteOffset, postingDoc.byteLength,
  );
  setup.device.queue.writeBuffer(
    setup.postingWBuf, 0,
    postingW.buffer, postingW.byteOffset, postingW.byteLength,
  );
}

export async function runTfidfSpMV(
  setup: TfidfSpMVSetup,
  queryTerms: Uint32Array,
  queryWeights: Float32Array,
): Promise<TfidfSpMVResult> {
  if (queryTerms.length !== queryWeights.length) {
    throw new Error("queryTerms and queryWeights must have matching length");
  }
  if (queryTerms.length === 0) {
    throw new Error("queryTerms cannot be empty");
  }
  if (queryTerms.length > setup.maxQueryTerms) {
    throw new Error(
      `query has ${queryTerms.length} terms but setup.maxQueryTerms is ${setup.maxQueryTerms}`,
    );
  }

  const { device } = setup;

  // Upload per-query buffers + uniform.
  device.queue.writeBuffer(setup.qTermBuf, 0, queryTerms.buffer, queryTerms.byteOffset, queryTerms.byteLength);
  device.queue.writeBuffer(setup.qWBuf,    0, queryWeights.buffer, queryWeights.byteOffset, queryWeights.byteLength);

  const params = new Uint32Array([queryTerms.length, setup.vocabSize, setup.nDocs, 0]);
  device.queue.writeBuffer(setup.paramsBuf, 0, params.buffer);

  const scatterBindGroup = device.createBindGroup({
    layout: setup.scatterBindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: setup.paramsBuf } },
      { binding: 1, resource: { buffer: setup.termOffsetsBuf } },
      { binding: 2, resource: { buffer: setup.postingDocBuf } },
      { binding: 3, resource: { buffer: setup.postingWBuf } },
      { binding: 4, resource: { buffer: setup.qTermBuf } },
      { binding: 5, resource: { buffer: setup.qWBuf } },
      { binding: 6, resource: { buffer: setup.scoresFixedBuf } },
    ],
  });
  const postpassBindGroup = device.createBindGroup({
    layout: setup.postpassBindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: setup.postpassParamsBuf } },
      { binding: 1, resource: { buffer: setup.scoresFixedBuf } },
      { binding: 2, resource: { buffer: setup.scoresF32Buf } },
    ],
  });

  const t0 = performance.now();
  device.pushErrorScope("validation");

  const encoder = device.createCommandEncoder({ label: "tfidfSpMV-encoder" });

  // Clear scores_fixed to zero — atomicAdd accumulates per-query.
  encoder.clearBuffer(setup.scoresFixedBuf, 0, setup.nDocs * 4);

  // Scatter pass: dispatch (WORKGROUPS_PER_TERM, Q, 1).
  const scatter = encoder.beginComputePass({ label: "tfidfSpMV-scatter-pass" });
  scatter.setPipeline(setup.scatterPipeline);
  scatter.setBindGroup(0, scatterBindGroup);
  scatter.dispatchWorkgroups(WORKGROUPS_PER_TERM, queryTerms.length, 1);
  scatter.end();

  // Postpass: convert u32 fixed → f32. Implicit barrier between passes (WebGPU spec §21.5).
  const postpass = encoder.beginComputePass({ label: "tfidfSpMV-postpass-pass" });
  postpass.setPipeline(setup.postpassPipeline);
  postpass.setBindGroup(0, postpassBindGroup);
  postpass.dispatchWorkgroups(Math.ceil(setup.nDocs / WG));
  postpass.end();

  encoder.copyBufferToBuffer(setup.scoresF32Buf, 0, setup.readbackBuf, 0, setup.nDocs * 4);
  device.queue.submit([encoder.finish()]);

  const validationError = await device.popErrorScope();
  if (validationError) {
    throw new Error(`WebGPU validation error during tfidfSpMV: ${validationError.message}`);
  }

  await setup.readbackBuf.mapAsync(GPUMapMode.READ);
  const scores = new Float32Array(setup.readbackBuf.getMappedRange().slice(0));
  setup.readbackBuf.unmap();
  const t1 = performance.now();

  // Diagnostic stats.
  let min = Infinity;
  let max = -Infinity;
  let nonzeroCount = 0;
  for (let i = 0; i < scores.length; i++) {
    const v = scores[i] ?? 0;
    if (v !== 0) nonzeroCount++;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (nonzeroCount === 0) {
    throw new Error(
      "tfidfSpMV produced all-zero scores. Either no postings matched the query " +
      "(check term ids vs vocabSize and CSR offsets) or the kernel didn't run " +
      "(check the browser console for WebGPU validation errors).",
    );
  }

  return {
    scores,
    totalMs: t1 - t0,
    scoreRange: {
      min: min === Infinity ? 0 : min,
      max: max === -Infinity ? 0 : max,
    },
    nonzeroCount,
  };
}

export function disposeTfidfSpMV(setup: TfidfSpMVSetup): void {
  setup.paramsBuf.destroy();
  setup.postpassParamsBuf.destroy();
  setup.qTermBuf.destroy();
  setup.qWBuf.destroy();
  setup.termOffsetsBuf.destroy();
  setup.postingDocBuf.destroy();
  setup.postingWBuf.destroy();
  setup.scoresFixedBuf.destroy();
  setup.scoresF32Buf.destroy();
  setup.readbackBuf.destroy();
}

async function assertNoCompileErrors(module: GPUShaderModule, label: string): Promise<void> {
  const info = await module.getCompilationInfo();
  const errors = info.messages.filter((m) => m.type === "error");
  if (errors.length > 0) {
    const text = errors.map((e) => `  L${e.lineNum}:${e.linePos} ${e.message}`).join("\n");
    throw new Error(`${label} WGSL compile errors:\n${text}`);
  }
  const warnings = info.messages.filter((m) => m.type === "warning");
  if (warnings.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[${label}] compile warnings:`, warnings);
  }
}

/**
 * Build a synthetic random CSR posting list for benching.
 * Returns the three CSR arrays sized to {vocabSize, totalPostings, nDocs}.
 *
 * Each term gets ~totalPostings/vocabSize postings on average; postings are
 * uniformly random doc ids with weights drawn from U(0, 1). Useful for the
 * /search bench page while the real storage-layer CSR build is in flight.
 */
export function buildRandomCSR(
  vocabSize: number,
  totalPostings: number,
  nDocs: number,
  seed = 1,
): { termOffsets: Uint32Array; postingDoc: Uint32Array; postingW: Float32Array } {
  // Mulberry32 — small, deterministic seedable PRNG.
  let s = seed >>> 0;
  const rand = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const termOffsets = new Uint32Array(vocabSize + 1);
  const postingDoc = new Uint32Array(totalPostings);
  const postingW = new Float32Array(totalPostings);

  // Spread postings across terms with a slight skew toward earlier terms
  // (so a query that picks low term ids gets more matches).
  const avg = totalPostings / vocabSize;
  let cursor = 0;
  for (let t = 0; t < vocabSize; t++) {
    termOffsets[t] = cursor;
    const remainingTerms = vocabSize - t;
    const remainingPostings = totalPostings - cursor;
    // Random count in [0, 2*avg], capped by remaining budget.
    const target = Math.min(
      remainingPostings - (remainingTerms - 1), // leave at least 1 per remaining term
      Math.max(1, Math.floor(rand() * 2 * avg)),
    );
    const count = Math.max(1, Math.min(remainingPostings, target));
    for (let i = 0; i < count; i++) {
      postingDoc[cursor + i] = Math.floor(rand() * nDocs);
      postingW[cursor + i] = rand();
    }
    cursor += count;
  }
  // Pad any leftover budget into the last term.
  if (cursor < totalPostings) {
    const lastStart = termOffsets[vocabSize - 1] ?? 0;
    for (let i = cursor; i < totalPostings; i++) {
      postingDoc[i] = Math.floor(rand() * nDocs);
      postingW[i] = rand();
    }
    cursor = totalPostings;
    void lastStart;
  }
  termOffsets[vocabSize] = cursor;
  return { termOffsets, postingDoc, postingW };
}
